// Editing a CUSTOM alarm in place, on the detail page (custom + conditional
// alarms, phase 7). Until this shipped the only way to change a custom alarm was
// to delete it and build it again — which loses its history and its id, and
// which the page had to apologise for in so many words.
//
// It is the builder's form, not a smaller one: the same draft reducer, the same
// `validate`, the same condition rows, the same error mapping. An editor that
// offered fewer fields than the builder would quietly make some alarms
// uneditable, and one with its own validation would drift from the API's.
//
// What it deliberately does NOT own: the live evaluation panel above it (that
// reads the STORED definition, so it keeps telling the truth while a draft is
// half-typed) and the save request itself (the page owns the PATCH, its banner
// and its refresh). This component is the fields.
//
// The one visible difference from the builder is that every section is on screen
// at once. The builder discloses progressively because it starts empty; an
// existing alarm has already answered every question, and hiding an answer
// behind a step the operator has to re-walk would be worse than showing it.
//
// UNITS (ADR 0189). The trigger's boxes, the chip beside each one, its "now …"
// hint and the review sentence are all in the ACCOUNT's unit for what the pinned
// signal measures — and in the boat's own unit when there is no account
// conversion for it, which is what a metric account sees everywhere.
//
// That works on a BASELINE MODEL, not on arithmetic at the form's edge. The
// draft carries the account settings AND the signal's declared store unit
// (`CustomUnitContext`), and `initFromCustomDef` records, per box, the exact
// TEXT it put there beside the stored NUMBER that text came from. A converted
// rendering is deliberately rounded — a stored 20 m reads "66", and 66 ft is
// 20.1168 m — so it does NOT round-trip; the baseline is what makes that safe.
// `draftSetpointStored` re-emits the original number verbatim for a box still
// holding the text it opened with, so an untouched save moves nothing and the
// boat's ruleset digest does not budge. NEVER read `Number(draft.setpoint)`
// here or anywhere else: on a converted draft that is the box's number, not the
// wire's.
//
// The CONDITION rows are the deliberate exception. A clause's threshold is typed
// in the signal's own store unit on every surface (`customAlarmSetup`,
// `ConditionsOptions`, `LivePanelOptions.units`), so its box, its chip and its
// "now …" hint stay there together. Converting only the reading beside a clause
// would put two units on one row, which is the failure this whole change exists
// to prevent.

import type { VesselType } from '@neomaris/protocol';

import { observationReading, observationText, type LiveObservation } from '../../lib/alarmLive';
import { conditionsClause, customAlarmSentence } from '../../lib/alarmSentence';
import type { CreateErrorView } from '../../lib/customAlarmErrors';
import {
  CUSTOM_MODE_UNIVERSE,
  CUSTOM_TIMING_LABELS,
  DEFAULT_STALE_SECONDS,
  NOTIFY_CHOICES,
  addCondition,
  clauseHolds,
  conditionField,
  conditionIndexOf,
  conditionSignals,
  customValueUnits,
  draftConditions,
  draftThresholds,
  errorFor,
  hasErrors,
  notifyLabel,
  removeCondition,
  setClearDelaySeconds,
  setClearpoint,
  setConditionLogic,
  setConditionOperator,
  setConditionSignal,
  setConditionValue,
  setEnabledModes,
  setNotify,
  setSetDelaySeconds,
  setSetpoint,
  setSeverity,
  setSignal,
  setStaleSeconds,
  setTitle,
  setTriggerClass,
  toggleMode,
  triggerHelp,
  triggerLabel,
  validate,
  type ConditionLogic,
  type ConditionOperator,
  type CustomAlarmDraft,
  type CustomAlarmField,
} from '../../lib/customAlarmForm';
import {
  signalDisplayName,
  signalLabel,
  suggestedTriggerClasses,
  type Signal,
  type TriggerClass,
} from '../../lib/signalCatalog';
import { ConditionBuilder } from './ConditionBuilder';
import { SignalPicker } from './SignalPicker';
import { SeveritySelect, WatchScheduleField } from './formFields';

export interface CustomAlarmEditFormProps {
  draft: CustomAlarmDraft;
  onDraftChange: (draft: CustomAlarmDraft) => void;
  /** The boat's signal catalog — the same accepted set the compiler binds against. */
  signals: readonly Signal[];
  onSave: (draft: CustomAlarmDraft) => void;
  onCancel: () => void;
  busy: boolean;
  /** A server rejection, already placed on a field by `mapPatchError`. */
  serverError: CreateErrorView | null;
  /** The current reading for any path — the "now …" hints and the clause chips. */
  observationFor: (path: string) => LiveObservation;
  /** The page's clock, so every relative wording on screen agrees. */
  now: number;
  /**
   * What kind of boat this is (ADR 0173), or null when unknown — passed to the
   * watch-schedule field, the only control here that speaks about sailing.
   */
  vesselType?: VesselType | null;
}

/** The human name for a catalog signal, device included when the boat named it. */
function labelOf(signal: Signal): string {
  return signalDisplayName(signal);
}

export function CustomAlarmEditForm({
  draft,
  onDraftChange,
  signals,
  onSave,
  onCancel,
  busy,
  serverError,
  observationFor,
  now,
  vesselType,
}: CustomAlarmEditFormProps) {
  // Everything on this form was already answered once, so every field is shown
  // touched: an existing alarm whose signal stopped reporting should say so on
  // arrival, not wait for the operator to click the field that broke.
  const errors = validate(draft, signals);
  const signal = signals.find((s) => s.path === draft.signalPath);
  const classes: readonly TriggerClass[] =
    signal === undefined ? [] : suggestedTriggerClasses(signal.kind, signal.path);
  const clauseCatalog = conditionSignals(signals);

  // How the two value boxes read, decided ONCE — the account's unit where a
  // conversion applies, the signal's own store unit otherwise. The chip beside
  // each box takes its label from here, and so does the reading under it, so a
  // number and the unit printed after it can never come from two places.
  // The catalog is the only source of the draft's `storeUnit`, and a draft
  // seeded before it landed carries none — so fall back to the signal row this
  // component was handed rather than dropping the chip off the box entirely.
  const boxUnits = customValueUnits(draft);
  const unit = boxUnits.unitSuffix.length > 0 ? boxUnits.unitSuffix : (signal?.unit ?? null);

  const signalAt = (path: string | undefined): Signal | undefined =>
    path === undefined || path.length === 0 ? undefined : signals.find((s) => s.path === path);

  /**
   * The reading beside a CLAUSE field, worded exactly as the live panel words
   * it — and in the same STORE unit the clause's own threshold box is typed in.
   */
  const readingHint = (path: string | undefined): string | null => {
    if (path === undefined || path.length === 0) return null;
    const text = observationText(observationFor(path), signalAt(path)?.unit ?? null, now);
    return text.charAt(0).toLowerCase() + text.slice(1);
  };

  /**
   * The reading beside the SET-POINT box, in whatever unit that box is in.
   * `boxUnits` already resolved the category and the store unit from the pinned
   * path, so this is the same decision the box's own chip made rather than a
   * second one taken here.
   */
  const triggerHint = ((): string | null => {
    const path = draft.signalPath;
    if (path === undefined || path.length === 0) return null;
    // A units-less draft (recipeUnlock) carries no storeUnit; fall back to the
    // signal's own unit like the chip at `unit` above does, or the hint prints
    // a bare number under a chip that still says "V".
    const text = observationText(
      observationFor(path),
      boxUnits.storeUnit ?? signalAt(path)?.unit ?? null,
      now,
      boxUnits.category,
      boxUnits.settings
    );
    return text.charAt(0).toLowerCase() + text.slice(1);
  })();
  // The same three-way verdict the live panel shows, from the same reading — a
  // last-fired or missing value judges nothing (`observationReading`).
  const clauseVerdicts = draft.conditions.map((row) =>
    clauseHolds(
      row,
      row.signalPath === undefined ? undefined : observationReading(observationFor(row.signalPath)),
      now
    )
  );

  function fieldError(field: CustomAlarmField): string | undefined {
    if (serverError?.field === field) return serverError.message;
    return errorFor(errors, field);
  }

  const firstError = errors[0];
  const blocked = hasErrors(errors);
  const formError = serverError !== null && serverError.field === undefined ? serverError : null;
  const blockedMessage =
    firstError === undefined
      ? undefined
      : conditionIndexOf(firstError.field) === null
        ? firstError.message
        : `Condition ${(conditionIndexOf(firstError.field) ?? 0) + 1}: ${firstError.message}`;

  // What the alarm will say once saved — the builder's review sentence, so the
  // two surfaces describe the same alarm in the same words.
  const sentence =
    signal !== undefined && draft.triggerClass !== undefined
      ? customAlarmSentence(
          { triggerClass: draft.triggerClass, path: signal.path },
          draftThresholds(draft),
          {
            label: labelOf(signal),
            // The STORE unit: `draftThresholds` hands back store-domain numbers
            // (through the baseline guard), and `category` + `units` are what
            // restate them. Passing the box's already-converted label here would
            // put an account unit on an unconverted number.
            unit: signal.unit,
            ...(boxUnits.category === undefined ? {} : { category: boxUnits.category }),
            ...(boxUnits.settings === undefined ? {} : { units: boxUnits.settings }),
            conditionClause: conditionsClause(draftConditions(draft), {
              labelFor: (path) => {
                const found = signalAt(path);
                return found === undefined ? signalLabel(path) : labelOf(found);
              },
              unitFor: (path) => signalAt(path)?.unit ?? null,
            }),
          }
        )
      : null;

  return (
    <form
      className="alarm-form alarm-custom-form"
      data-testid="alarm-detail-edit-custom"
      noValidate
      onSubmit={(e) => {
        e.preventDefault();
        if (busy || blocked) return;
        onSave(draft);
      }}
    >
      <fieldset className="alarm-form-fieldset">
        <legend>What the boat watches</legend>
        <SignalPicker
          signals={signals}
          value={draft.signalPath}
          onPick={(picked) => onDraftChange(setSignal(draft, picked))}
          label="Signal"
          testidPrefix="alarm-detail-edit-signal"
        />
        {fieldError('signalPath') !== undefined ? (
          <p className="alarm-form-err" data-testid="alarm-detail-edit-signal-err">
            {fieldError('signalPath')}
          </p>
        ) : null}
      </fieldset>

      <fieldset className="alarm-form-fieldset">
        <legend>What sets it off</legend>
        <div className="alarm-custom-classes" role="radiogroup" aria-label="What sets it off">
          {classes.map((cls) => (
            <label
              key={cls}
              className={`alarm-custom-class${draft.triggerClass === cls ? ' alarm-custom-class--on' : ''}`}
            >
              <input
                type="radio"
                name="alarm-detail-edit-class"
                value={cls}
                checked={draft.triggerClass === cls}
                data-testid={`alarm-detail-edit-class-${cls}`}
                onChange={() => onDraftChange(setTriggerClass(draft, cls))}
              />
              <span className="alarm-custom-class__body">
                <span className="alarm-custom-class__title">{triggerLabel(cls)}</span>
                <span className="alarm-custom-class__sub">{triggerHelp(cls)}</span>
              </span>
            </label>
          ))}
        </div>
        {fieldError('triggerClass') !== undefined ? (
          <p className="alarm-form-err">{fieldError('triggerClass')}</p>
        ) : null}
      </fieldset>

      <fieldset className="alarm-form-fieldset">
        <legend>
          {draft.triggerClass === 'DataUnavailable' ? 'How long to wait' : 'The numbers'}
        </legend>

        {draft.triggerClass === 'High' || draft.triggerClass === 'Low' ? (
          <div className="alarm-custom-field">
            <label htmlFor="alarm-detail-edit-setpoint">
              {draft.triggerClass === 'High' ? 'Set off above' : 'Set off below'}
            </label>
            <div className="alarm-custom-row">
              <input
                id="alarm-detail-edit-setpoint"
                className="form-input"
                type="number"
                inputMode="decimal"
                step="any"
                value={draft.setpoint ?? ''}
                data-testid="alarm-detail-edit-setpoint"
                onChange={(e) => onDraftChange(setSetpoint(draft, e.target.value))}
              />
              {unit === null || unit === '' ? null : (
                <span className="alarm-form-unit">{unit}</span>
              )}
              {triggerHint === null ? null : (
                <span className="alarm-custom-now" data-testid="alarm-detail-edit-now">
                  {triggerHint}
                </span>
              )}
            </div>
            {fieldError('setpoint') !== undefined ? (
              <p className="alarm-form-err">{fieldError('setpoint')}</p>
            ) : null}
          </div>
        ) : null}

        {draft.triggerClass === 'DataUnavailable' ? (
          <div className="alarm-custom-field">
            <label htmlFor="alarm-detail-edit-stale">{CUSTOM_TIMING_LABELS.stale}</label>
            <div className="alarm-custom-row">
              <input
                id="alarm-detail-edit-stale"
                className="form-input"
                type="number"
                inputMode="numeric"
                step="1"
                min="0"
                value={draft.staleSeconds ?? ''}
                data-testid="alarm-detail-edit-stale"
                onChange={(e) => onDraftChange(setStaleSeconds(draft, e.target.value))}
              />
              <span className="alarm-form-unit">seconds</span>
            </div>
            <p className="alarm-form-hint">
              Left empty, your boat waits {DEFAULT_STALE_SECONDS} seconds (5 minutes).
            </p>
            {fieldError('staleSeconds') !== undefined ? (
              <p className="alarm-form-err">{fieldError('staleSeconds')}</p>
            ) : null}
          </div>
        ) : (
          <details
            className="alarm-custom-advanced"
            // A rejection aimed at a field in here must never land invisibly
            // behind a collapsed summary.
            open={
              serverError !== null &&
              (serverError.field === 'clearpoint' ||
                serverError.field === 'setDelaySeconds' ||
                serverError.field === 'clearDelaySeconds')
                ? true
                : undefined
            }
          >
            <summary>Fine tuning</summary>

            {draft.triggerClass === 'High' || draft.triggerClass === 'Low' ? (
              <div className="alarm-custom-field">
                <label htmlFor="alarm-detail-edit-clearpoint">Clears at</label>
                <div className="alarm-custom-row">
                  <input
                    id="alarm-detail-edit-clearpoint"
                    className="form-input"
                    type="number"
                    inputMode="decimal"
                    step="any"
                    value={draft.clearpoint ?? ''}
                    data-testid="alarm-detail-edit-clearpoint"
                    onChange={(e) => onDraftChange(setClearpoint(draft, e.target.value))}
                  />
                  {unit === null || unit === '' ? null : (
                    <span className="alarm-form-unit">{unit}</span>
                  )}
                </div>
                <p className="alarm-form-hint">
                  Left blank, it clears at the set point — it may chatter near the line.
                </p>
                {fieldError('clearpoint') !== undefined ? (
                  <p className="alarm-form-err">{fieldError('clearpoint')}</p>
                ) : null}
              </div>
            ) : null}

            <div className="alarm-custom-field">
              <label htmlFor="alarm-detail-edit-setdelay">{CUSTOM_TIMING_LABELS.setDelay}</label>
              <div className="alarm-custom-row">
                <input
                  id="alarm-detail-edit-setdelay"
                  className="form-input"
                  type="number"
                  inputMode="numeric"
                  step="1"
                  min="0"
                  value={draft.setDelaySeconds}
                  data-testid="alarm-detail-edit-setdelay"
                  onChange={(e) => onDraftChange(setSetDelaySeconds(draft, e.target.value))}
                />
                <span className="alarm-form-unit">seconds</span>
              </div>
              {fieldError('setDelaySeconds') !== undefined ? (
                <p className="alarm-form-err">{fieldError('setDelaySeconds')}</p>
              ) : null}
            </div>

            <div className="alarm-custom-field">
              <label htmlFor="alarm-detail-edit-cleardelay">
                {CUSTOM_TIMING_LABELS.clearDelay}
              </label>
              <div className="alarm-custom-row">
                <input
                  id="alarm-detail-edit-cleardelay"
                  className="form-input"
                  type="number"
                  inputMode="numeric"
                  step="1"
                  min="0"
                  value={draft.clearDelaySeconds}
                  data-testid="alarm-detail-edit-cleardelay"
                  onChange={(e) => onDraftChange(setClearDelaySeconds(draft, e.target.value))}
                />
                <span className="alarm-form-unit">seconds</span>
              </div>
              {fieldError('clearDelaySeconds') !== undefined ? (
                <p className="alarm-form-err">{fieldError('clearDelaySeconds')}</p>
              ) : null}
            </div>
          </details>
        )}
      </fieldset>

      <ConditionBuilder
        signals={clauseCatalog}
        rows={draft.conditions}
        logic={draft.conditionLogic}
        verdicts={clauseVerdicts}
        readingHints={draft.conditions.map((row) => readingHint(row.signalPath))}
        errors={draft.conditions.map((_, index) => fieldError(conditionField(index)))}
        sectionError={fieldError('conditions')}
        onAdd={() => onDraftChange(addCondition(draft))}
        onRemove={(index) => onDraftChange(removeCondition(draft, index))}
        onPickSignal={(index, picked) => onDraftChange(setConditionSignal(draft, index, picked))}
        onOperator={(index, operator: ConditionOperator) =>
          onDraftChange(setConditionOperator(draft, index, operator))
        }
        onValue={(index, value) => onDraftChange(setConditionValue(draft, index, value))}
        onLogic={(logic: ConditionLogic) => onDraftChange(setConditionLogic(draft, logic))}
      />

      <fieldset className="alarm-form-fieldset">
        <legend>Name it</legend>

        <div className="form-group">
          <label htmlFor="alarm-detail-edit-title">Name</label>
          <input
            id="alarm-detail-edit-title"
            className="form-input"
            type="text"
            value={draft.title}
            data-testid="alarm-detail-edit-title"
            onChange={(e) => onDraftChange(setTitle(draft, e.target.value))}
          />
          {fieldError('title') !== undefined ? (
            <p className="alarm-form-err">{fieldError('title')}</p>
          ) : null}
        </div>

        <SeveritySelect
          value={draft.severity}
          onChange={(severity) => onDraftChange(setSeverity(draft, severity))}
          id="alarm-detail-edit-severity"
          testId="alarm-detail-edit-severity"
          label="How serious is it?"
        />

        <WatchScheduleField
          name="alarm-detail-edit-watch"
          modeUniverse={CUSTOM_MODE_UNIVERSE}
          enabledModes={draft.enabledModes}
          onSetModes={(modes) => onDraftChange(setEnabledModes(draft, modes))}
          onToggleMode={(m) => onDraftChange(toggleMode(draft, m))}
          error={errorFor(errors, 'enabledModes')}
          testIdPrefix="alarm-detail-edit-mode-"
          presetTestIdPrefix="alarm-detail-edit-watch-"
          vesselType={vesselType}
        />

        <div className="form-group">
          <label htmlFor="alarm-detail-edit-notify">When it goes off</label>
          <select
            id="alarm-detail-edit-notify"
            className="form-input"
            value={draft.notify}
            data-testid="alarm-detail-edit-notify"
            onChange={(e) =>
              onDraftChange(setNotify(draft, e.target.value as CustomAlarmDraft['notify']))
            }
          >
            {/* A definition stored with an urgency this form does not offer
                (`silent`, `escalating`) keeps it: showing it as an option means
                merely opening the editor cannot silently change it. */}
            {(NOTIFY_CHOICES.includes(draft.notify)
              ? NOTIFY_CHOICES
              : [draft.notify, ...NOTIFY_CHOICES]
            ).map((urgency) => (
              <option key={urgency} value={urgency}>
                {notifyLabel(urgency)}
              </option>
            ))}
          </select>
        </div>
      </fieldset>

      <div className="alarm-custom-review" data-testid="alarm-detail-edit-review">
        <h2 className="alarm-custom-review__head">What this alarm will do</h2>
        <p className="alarm-custom-sentence" data-testid="alarm-detail-edit-sentence">
          {sentence ?? 'Pick a signal and choose what sets it off.'}
        </p>

        {formError !== null ? (
          <p className="alarm-command-banner" role="alert" data-testid="alarm-detail-edit-error">
            {formError.message}
          </p>
        ) : null}

        <div className="alarm-form-actions">
          <button
            type="submit"
            className="btn btn-primary"
            disabled={busy || blocked}
            data-testid="alarm-detail-edit-save"
          >
            {busy ? 'Saving…' : 'Save changes'}
          </button>
          <button type="button" className="btn" disabled={busy} onClick={onCancel}>
            Cancel
          </button>
          {blocked && blockedMessage !== undefined ? (
            <span className="alarm-custom-blocked" data-testid="alarm-detail-edit-blocked">
              {blockedMessage}
            </span>
          ) : null}
        </div>
      </div>
    </form>
  );
}
